import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "@tanstack/react-router";
import { Button } from "@provenleap/ui";
import { CalendarHeart, Leaf } from "@phosphor-icons/react";
import { logoUrl, PICKER_PLATFORMS } from "../../lib/integrations";

const INTEGRATIONS = PICKER_PLATFORMS.map((p) => ({
  name: p.displayName,
  domain: p.domain,
}));

const ICON_SIZE = 22;
const ARCH_RADIUS = 440;
const ARC_START = 185;
const ARC_END = 355;
const ARC_SPAN = ARC_END - ARC_START;
const COUNT = INTEGRATIONS.length;
const SPEED = 1.5;
const MAX_FRAME_DELTA_SECONDS = 1 / 30;

export function HeroSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number>(0);
  const hoveredRef = useRef<number | null>(null);
  const [tooltip, setTooltip] = useState<{ index: number; top: number; left: number } | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const icons = Array.from(container.querySelectorAll<HTMLElement>("img[data-index]"));
    let lastTimestamp: number | null = null;
    let offset = 0;
    let running = false;
    const cx = 450;
    const cy = 560;
    const spacing = ARC_SPAN / COUNT;

    // The animated arch is `hidden md:block`, so it only exists at md+. Below md
    // it is display:none and the mobile CSS marquee takes over — don't burn rAF
    // frames or set spurious tooltip state while the arch isn't visible.
    // Client-only Vite SPA, so matchMedia is always available (no SSR guard).
    const desktop = window.matchMedia("(min-width: 768px)");

    function animate(timestamp: number) {
      if (!running) return;
      const elapsed = lastTimestamp === null
        ? 0
        : Math.min(Math.max((timestamp - lastTimestamp) / 1000, 0), MAX_FRAME_DELTA_SECONDS);
      lastTimestamp = timestamp;
      offset = (offset + elapsed * SPEED) % ARC_SPAN;

      for (let i = 0; i < COUNT; i++) {
        const basePos = spacing * i + offset;
        const wrappedPos = ((basePos % ARC_SPAN) + ARC_SPAN) % ARC_SPAN;
        const angle = ARC_START + wrappedPos;

        const rad = (angle * Math.PI) / 180;
        const x = cx + ARCH_RADIUS * Math.cos(rad) - ICON_SIZE / 2;
        const y = cy + ARCH_RADIUS * Math.sin(rad) - ICON_SIZE / 2;

        const fadeZone = 15;
        let opacity = 1;
        if (wrappedPos < fadeZone) {
          opacity = wrappedPos / fadeZone;
        } else if (wrappedPos > ARC_SPAN - fadeZone) {
          opacity = (ARC_SPAN - wrappedPos) / fadeZone;
        }

        const el = icons[i];
        if (el) {
          el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
          el.style.opacity = `${opacity}`;
        }

        // Keep a hovered tooltip attached without forcing a React render every frame.
        if (i === hoveredRef.current && el && tooltipRef.current) {
          const rect = el.getBoundingClientRect();
          tooltipRef.current.style.transform = `translate3d(${rect.left + rect.width / 2}px, ${rect.top - 8}px, 0) translate(-50%, -100%)`;
        }
      }

      frameRef.current = requestAnimationFrame(animate);
    }

    // Start only one desktop loop and resume from the current phase without catch-up jumps.
    function play() {
      if (running) return;
      running = true;
      lastTimestamp = null;
      frameRef.current = requestAnimationFrame(animate);
    }
    // Stop the loop and clear any lingering tooltip when we drop below md.
    function stop() {
      running = false;
      cancelAnimationFrame(frameRef.current);
      hoveredRef.current = null;
      setTooltip(null);
    }

    if (desktop.matches) play();

    function onBreakpointChange(event: MediaQueryListEvent) {
      if (event.matches) play();
      else stop();
    }
    desktop.addEventListener("change", onBreakpointChange);

    return () => {
      running = false;
      cancelAnimationFrame(frameRef.current);
      desktop.removeEventListener("change", onBreakpointChange);
    };
  }, []);

  return (
    // pt-20 on mobile clears the fixed 64px header so the short logo marquee
    // sits fully below it (desktop's tall arch absorbs the overlap at sm:pt-6).
    <div id="hero" className="pt-20 pb-16 sm:pt-6 sm:pb-20 scroll-mt-20">
      <div className="max-w-[1200px] mx-auto px-3 sm:px-8">
        <div className="flex flex-col items-center">
          <div className="text-center">
            {/* Desktop: animated arch of integration logos. Its hard 900px
                width would force the whole hero to 900px, so it's hidden below
                md where the mobile marquee below stands in. */}
            <div
              ref={containerRef}
              className="hidden md:block relative h-[280px] w-[900px] mx-auto mb-0"
              aria-label="Supported integrations"
            >
              {INTEGRATIONS.map((integration, i) => (
                <img
                  key={integration.name}
                  src={logoUrl(integration.domain)}
                  alt={integration.name}
                  width={ICON_SIZE}
                  height={ICON_SIZE}
                  data-index={i}
                  className="absolute left-0 top-0 rounded-sm cursor-pointer"
                  // Above the fold — load eagerly (loading="lazy" deferred the
                  // fetch and made the logos pop in late). Icons are now local
                  // vendored assets (see logoUrl / CUSTOM_LOGOS), so this paints
                  // immediately. Start invisible until the first rAF frame places
                  // each icon. Compositor transforms avoid per-frame layout work.
                  style={{ willChange: "transform, opacity", opacity: 0, transform: "translate3d(0, 0, 0)" }}
                  loading="eager"
                  decoding="async"
                  onMouseEnter={(event) => {
                    hoveredRef.current = i;
                    const rect = event.currentTarget.getBoundingClientRect();
                    setTooltip({
                      index: i,
                      top: rect.top - 8,
                      left: rect.left + rect.width / 2,
                    });
                  }}
                  onMouseLeave={() => {
                    hoveredRef.current = null;
                    setTooltip(null);
                  }}
                />
              ))}
            </div>

            {/* Mobile: contained, edge-faded logo marquee standing in for the
                arch. Decorative here — the Integrations section lists these
                accessibly — so aria-hidden + empty alt. The list is rendered
                twice so the .logo-marquee-track's -50% translate loops seamlessly. */}
            <div
              className="md:hidden relative w-full overflow-hidden h-14 mb-6"
              aria-hidden="true"
              style={{
                // Fade logos in/out at both edges, echoing the arch's fade.
                // Duplicated with Webkit prefix for Safari.
                maskImage:
                  "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
                WebkitMaskImage:
                  "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
              }}
            >
              <div className="logo-marquee-track absolute top-0 left-0 flex h-full w-max items-center gap-8">
                {[...INTEGRATIONS, ...INTEGRATIONS].map((integration, i) => (
                  <img
                    key={`${integration.name}-${i}`}
                    src={logoUrl(integration.domain)}
                    alt=""
                    width={24}
                    height={24}
                    className="rounded-sm shrink-0"
                    loading="eager"
                    decoding="async"
                  />
                ))}
              </div>
            </div>

            <h1 className="text-[26px] sm:text-[30px] md:text-[38px] font-bold text-foreground tracking-tight leading-[1.1] mb-6">
              Focus on{" "}
              <span className="uppercase">your</span>{" "}
              <em className="font-normal italic">clients.</em>
              <br />
              We&rsquo;ll handle the{" "}
              <em className="font-normal italic">reports.</em>
            </h1>
            <p className="text-[15px] sm:text-[17px] leading-relaxed text-foreground/85 max-w-[480px] mx-auto mb-8">
              Connect your clients&rsquo; platforms once. We pull the data, build
              beautiful branded reports, and deliver them on schedule. All
              integrations included, on every plan.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-14">
              <Button
                asChild
                variant="outline"
                size="lg"
                className="min-h-11 px-6 text-base rounded-2xl text-foreground/85 shadow-xs hover:bg-background hover:text-foreground/85 hover:-translate-y-0.5 hover:shadow-sm transition-all [&_svg]:!size-[18px]"
                style={{ border: "0.5px solid var(--hairline)" }}
              >
                <Link to="/waitlist">
                  <CalendarHeart size={18} className="text-foreground" />
                  Book a short demo
                </Link>
              </Button>
              <span className="text-sm text-foreground/70">or</span>
              <Button asChild variant="brand" size="lg" className="min-h-11 px-6 text-base rounded-2xl [&_svg]:!size-[18px]">
                <a href="#pricing">
                  <Leaf size={18} />
                  Try out the free plan
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {tooltip && createPortal(
        <div
          ref={tooltipRef}
          className="fixed z-[100] pointer-events-none"
          style={{
            top: 0,
            left: 0,
            transform: `translate3d(${tooltip.left}px, ${tooltip.top}px, 0) translate(-50%, -100%)`,
            willChange: "transform",
          }}
        >
          <div className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-2 h-2 bg-background border-r border-b border-input rotate-45 z-10" />
          <div className="relative bg-background text-foreground text-[13px] font-medium px-2.5 py-1 rounded-md border border-input shadow-xs whitespace-nowrap">
            {INTEGRATIONS[tooltip.index]?.name}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
